import React, { useCallback, useMemo } from "react";

import Select, { RefSelectProps } from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import TripType from "@mapmycustomers/shared/enum/mileageTracking/TripType";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { SimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import i18nService from "@app/config/I18nService";
import { getTripTypeDisplayName } from "@app/util/ui";

import getHumanReadableDescriptionForOptionsFilter, {
  OptionFilterOperator,
} from "./getHumanReadableDescriptionForOptionsFilter";
import styles from "./TeamsFilter.module.scss";

export const CLASSIFICATION_FILTER_OPERATORS = [FilterOperator.EQUALS];

const doesSupportValue = (value: any, operator: FilterOperator) =>
  typeof value === "string" &&
  Object.values(TripType).includes(value as TripType) &&
  CLASSIFICATION_FILTER_OPERATORS.includes(operator);

interface ClassificationFilterProps extends IFilterComponentProps {}

const ClassificationFilter: IFilterInstance = {
  doesSupportValue,
  getComponent:
    (): React.FC<ClassificationFilterProps> =>
    ({ className, focus, onChange, value }) => {
      const intl = useIntl();

      const options = useMemo(
        () =>
          [TripType.BUSINESS, TripType.PERSONAL].map((value) => ({
            label: getTripTypeDisplayName(intl, value),
            value,
          })),
        [intl]
      );

      const setRef = useCallback(
        (ref: null | RefSelectProps) => {
          if (focus && ref) {
            ref.focus();
          }
        },
        [focus]
      );

      return (
        <Select<TripType>
          className={cn(styles.container, className)}
          filterOption={defaultFilterOption}
          onChange={(tripType: TripType) => onChange?.({ ...value, value: tripType })}
          options={options}
          placeholder={intl.formatMessage({
            id: "filters.team.select.placeholder",
            defaultMessage: "Click or type to select teams",
            description: "Placeholder displayed in a TeamFilter's select field",
          })}
          ref={setRef}
          value={value.value}
        />
      );
    },
  getHumanReadableDescription: (value: SimpleCondition, field: IField) => {
    if (!doesSupportValue(value.value, value.operator)) {
      return undefined;
    }

    const intl = i18nService.getIntl();

    return getHumanReadableDescriptionForOptionsFilter(
      field,
      value.operator as OptionFilterOperator,
      [value.value && intl ? getTripTypeDisplayName(intl, value.value) : value.value]
    );
  },
};

export default ClassificationFilter;
